import { ChangeDetectionStrategy, Component, signal, inject } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NgForm, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { FormDialog } from '../../types';
import { UiDialogShellComponent } from '../../ui/dialog-shell/dialog-shell.component';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { UiInputDirective, UiTextareaDirective } from '../../ui/input/input.directive';
import { UiButtonDirective } from '../../ui/button/button.directive';

type FormDialogState = 'form' | 'sending' | 'done';

const DEFAULT_SUBMIT_ERROR = 'Something went wrong. Please try again.';

@Component({
  selector: 'app-form-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'form-dialog.component.html',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    UiDialogShellComponent,
    UiFieldComponent,
    UiIconComponent,
    UiInputDirective,
    UiTextareaDirective,
    UiButtonDirective,
  ],
})
export class FormDialogComponent {
  dialogRef = inject<DialogRef<void, FormDialogComponent>>(DialogRef);
  data = inject<FormDialog>(DIALOG_DATA);

  readonly descriptionId = 'form-dialog-description';

  readonly state = signal<FormDialogState>('form');
  readonly submitError = signal<string | null>(null);

  get fieldLabel(): string | null {
    const form = this.data.form;
    if (!form) return null;
    // A blank label is treated as absent so the aria fallback still applies.
    const label = form.label === undefined ? form.placeholder : form.label;
    return label?.trim() ? label : null;
  }

  // The input keeps an accessible name when the visible label is opted out —
  // falling back to the dialog title if there is no placeholder either.
  get fieldAriaLabel(): string | null {
    if (this.fieldLabel !== null) return null;
    const placeholder = this.data.form?.placeholder;
    return placeholder?.trim() ? placeholder : this.data.title;
  }

  async onSubmit(form: NgForm): Promise<void> {
    // Legacy behavior: fire-and-forget, close immediately.
    if (!this.data.success) {
      Promise.resolve(this.data.onSubmit(form)).catch(error => {
        console.error('FormDialog legacy submission failed:', error);
      });
      this.dialogRef.close();
      return;
    }

    // Awaited behavior: keep the dialog open through submission so success and
    // failure both render in place (the typed message is never lost).
    this.state.set('sending');
    this.submitError.set(null);

    try {
      await this.data.onSubmit(form);
      this.state.set('done');
    } catch {
      this.state.set('form');
      this.submitError.set(this.data.success.errorMessage ?? DEFAULT_SUBMIT_ERROR);
    }
  }

  onCancel(): void {
    if (this.data.onCancel) this.data.onCancel();
    this.dialogRef.close();
  }

  onDone(): void {
    this.dialogRef.close();
  }
}
